import HeaderLogo from '@/assets/logo_twm_suffix.png'
import { COLOR } from '@/libs/core/constants'
import { getImageSize, pixelToDxa, pixelToHalfPt } from '@/libs/utils/docxUtils'
import {
  AlignmentType,
  BorderStyle,
  Document,
  Footer,
  Header,
  ImageRun,
  PageNumber,
  Paragraph,
  Table,
  TableCell,
  TableRow,
  TextRun,
  WidthType,
} from 'docx'
import Chapter1Page from './Pages/Course/Chapter1Page'
import CoverPage from './Pages/Course/CoverPage'
import DisclaimerPage from './Pages/Course/DisclaimerPage'
import { tableWidthDxa } from './constants'
import max from 'lodash/max'
import AttachmentPage from './Pages/Course/AttachmentPage'

const generate = async ({ reportData }) => {
  const { metaData } = reportData || {}
  const { firstPartyAbbreviation, secondPartyFullName } = metaData || {}

  const { width, height } = await getImageSize({ image: HeaderLogo, width: 273 })
  const imgData = await (await fetch(HeaderLogo)).blob()
  const nameWidth = max([firstPartyAbbreviation.length, 5]) * pixelToDxa(30)

  const zeroBorder = {
    top: { size: 0, style: BorderStyle.NONE, color: 'FFFFFF' },
    bottom: { size: 0, style: BorderStyle.NONE, color: 'FFFFFF' },
    left: { size: 0, style: BorderStyle.NONE, color: 'FFFFFF' },
    right: { size: 0, style: BorderStyle.NONE, color: 'FFFFFF' },
  }

  return new Document({
    features: {
      updateFields: true,
    },
    styles: {
      default: { document: { run: { size: pixelToHalfPt(20), font: 'Calibri' } } },
      paragraphStyles: [
        {
          id: 'coverTitle',
          name: 'Cover Title',
          basedOn: 'Normal',
          run: { size: pixelToHalfPt(30), characterSpacing: 40, font: 'Calibri' },
        },
        {
          id: 'coverSubtitle',
          name: 'Cover Subtitle',
          basedOn: 'Normal',
          run: { size: pixelToHalfPt(16), characterSpacing: 20 },
        },
        {
          id: 'headerText',
          name: 'Header Text',
          basedOn: 'Normal',
          run: { size: pixelToHalfPt(22), bold: true },
        },
        {
          id: 'horizontalRule',
          name: 'Horizontal Rule',
          basedOn: 'Normal',
          run: { size: 1 },
        },
        {
          id: 'sectionHeader',
          name: 'Section Header',
          paragraph: {
            spacing: { before: pixelToDxa(40), after: pixelToDxa(40) },
          },
          run: { size: pixelToHalfPt(25), bold: true },
        },
        {
          id: 'textParagraph',
          name: 'Text Paragraph',
          basedOn: 'Normal',
          paragraph: { spacing: { line: pixelToDxa(50) }, indent: { left: pixelToDxa(30) } },
        },
        {
          id: 'textParagraphIndent',
          name: 'Text Paragraph Indent',
          basedOn: 'Normal',
          paragraph: { spacing: { line: pixelToDxa(50) }, indent: { firstLine: pixelToDxa(30) } },
        },
        {
          id: 'default',
          name: 'Default',
          basedOn: 'Normal',
          run: { size: pixelToHalfPt(20) },
        },
      ],
    },
    sections: [
      {
        properties: {
          titlePage: true,
          page: {
            size: { height: '297mm', width: '210mm' },
            pageNumbers: { start: 0 },
            margin: { left: pixelToDxa(100), right: pixelToDxa(100) },
          },
        },
        headers: {
          default: new Header({
            children: [
              new Paragraph({
                children: [
                  new ImageRun({
                    data: imgData,
                    transformation: {
                      width,
                      height,
                    },
                  }),
                ],
              }),
              new Paragraph(''),
              new Table({
                borders: zeroBorder,
                columnWidths: [tableWidthDxa - nameWidth + 100, nameWidth],
                width: { size: tableWidthDxa + 100, type: WidthType.DXA },
                rows: [
                  new TableRow({
                    children: [
                      new TableCell({
                        borders: zeroBorder,
                        children: [
                          new Paragraph({
                            style: 'horizontalRule',
                            border: {
                              bottom: {
                                color: '#000000',
                                space: 7,
                                style: 'single',
                                size: 1,
                              },
                            },
                          }),
                        ],
                      }),
                      new TableCell({
                        borders: zeroBorder,
                        children: [
                          new Paragraph({
                            spacing: { after: pixelToDxa(6) },
                            alignment: AlignmentType.RIGHT,
                            style: 'headerText',
                            children: [
                              new TextRun({ text: firstPartyAbbreviation, color: COLOR.primary }),
                            ],
                          }),
                        ],
                      }),
                    ],
                  }),
                  new TableRow({
                    children: [
                      new TableCell({
                        borders: zeroBorder,
                        children: [],
                      }),
                      new TableCell({
                        borders: zeroBorder,
                        children: [
                          new Paragraph({
                            spacing: { after: pixelToDxa(20) },
                            alignment: AlignmentType.RIGHT,
                            style: 'headerText',
                            children: [
                              new TextRun({
                                text: '結果報告',
                                color: COLOR.secondary,
                                rightToLeft: true,
                              }),
                            ],
                          }),
                        ],
                      }),
                    ],
                  }),
                ],
              }),
            ],
          }),
        },
        footers: {
          default: new Footer({
            children: [
              new Paragraph({
                alignment: AlignmentType.RIGHT,
                children: [
                  new TextRun('本文件之智慧財產權屬 '),
                  new TextRun({ text: secondPartyFullName, color: COLOR.primary }),
                ],
              }),
              new Paragraph(''),
              new Paragraph(''),

              new Paragraph({
                alignment: AlignmentType.CENTER,
                children: [new TextRun({ children: [PageNumber.CURRENT] })],
              }),
            ],
          }),
        },
        children: [
          //   new TableOfContents('Summary', {
          //     hyperlink: true,
          //     headingStyleRange: '1-5',
          //     stylesWithLevels: [new StyleLevel('sectionHeader', 1)],
          //   }),
          ...(await CoverPage({ reportData })),
          ...DisclaimerPage({ reportData }),
          ...Chapter1Page({ reportData }),
          ...AttachmentPage({ reportData }),
        ],
      },
    ],
  })
}

export { generate }
